import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import InfoIcon from '@mui/icons-material/Info'
import { Box, Grid, MenuItem, Select, TextField, Tooltip } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

import NormalText from '../../../../common/Typography/NormalText'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DesktopDatePicker
        label={label}
        inputFormat='dd/MM/yyyy'
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  benefitSummary: '',
  benefitCategory: '',
  benefitProductID: '0',
  benefitValue: '',
  benefitUnit: ''
}

const NewBenefitTypeSchema = Yup.object().shape({
  benefitSummary: Yup.string().required('Benefit summary is required'),
  benefitCategory: Yup.string().required('Benefit category is required'),
  benefitProductID: Yup.string().when('benefitCategory', {
    is: 'DATA',
    then: Yup.string().required('Benefit product ID is required'),
    otherwise: Yup.string()
  }),
  benefitValue: Yup.string().required('Benefit value is required'),
  benefitUnit: Yup.string().required('Benefit unit is required')
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

export const NewBenefitTypeForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  const faberToken = OrbitSession.get('faberToken')
  const { data: catalogIds } = useQuery(['getCatalogsBase'], async () =>
    RewardsAPI.getCatalogs(faberToken, true)
  )

  interface CatalogRow {
    id: string
    name: string
    description: string
    tags: string[]
  }
  const [catalogRows, setCatalogRows] = React.useState<CatalogRow[]>([])
  const [catalogSelectionModel, setCatalogSelectionModel] =
    React.useState<string>('')
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200
    },
    {
      field: 'tenant',
      headerName: 'Tenant',
      width: 100
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 500
    },
    {
      field: 'packageType',
      headerName: 'Package Type',
      width: 120
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 200
    },
  ]

  useEffect(() => {
    if (catalogIds) {
      setCatalogRows(catalogIds)
    }
  }, [catalogIds])

  const { mutate: createPromoCode } = useMutation(RewardsAPI.createBenefit, {
    onSuccess: () => {
      toast.success('Benefit type created successfully')
      handleClose()
    },
    onError: () => {
      toast.error('Error creating benefit type')
    }
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewBenefitTypeFormikProps) => {
    const benefitId = catalogSelectionModel ? catalogSelectionModel : '0'
    const allValues = {
      benefitSummary: values.benefitSummary,
      benefitCategory: values.benefitCategory,
      benefitProductID: benefitId,
      benefitValue: values.benefitValue,
      benefitUnit: values.benefitUnit,
      faberToken: faberToken
    }
    createPromoCode(allValues)
  }

  /**
   * Event handlers
   */
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewBenefitTypeSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={3}>
              <NormalText isBold>Benefit Name</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'benefit-summary'
                  }}
                  name='benefitSummary'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.benefitSummary && touched.benefitSummary)}
                  helperText={
                    errors.benefitSummary && touched.benefitSummary
                      ? errors.benefitSummary
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <NormalText isBold>Pack</NormalText>
                <Tooltip title='Underlying product. Can be left empty if the benefit is a discount.'>
                  <InfoIcon style={{ marginLeft: '5px' }} />
                </Tooltip>
              </div>
            </Grid>
            <Grid item xs={12} height={640}>
              <DataGrid
                rows={catalogRows}
                columns={columns}
                rowsPerPageOptions={[10]}
                pageSize={10}
                selectionModel={catalogSelectionModel}
                onSelectionModelChange={(newSelection) => {
                  setCatalogSelectionModel(newSelection[0] as string)
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Benefit Type</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                defaultValue=''
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='benefitCategory'
                label=''
              >
                <MenuItem value='DATA'>DATA</MenuItem>
                <MenuItem value='DISCOUNT'>DISCOUNT</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Amount</NormalText>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Grid item xs={12}>
                <Box sx={{ width: '90%' }}>
                  <StyledTextField
                    inputProps={{
                      'data-testid': 'benefit-value'
                    }}
                    name='benefitValue'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    fullWidth
                    variant='outlined'
                    error={!!(errors.benefitValue && touched.benefitValue)}
                    helperText={
                      errors.benefitValue && touched.benefitValue
                        ? errors.benefitValue
                        : null
                    }
                  />
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={3}>
              <Grid item xs={3}>
                <NormalText isBold></NormalText>
              </Grid>
              <Grid item xs={12}>
                <Select
                  defaultValue=''
                  variant='standard'
                  sx={{ width: '80%' }}
                  onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                  name='benefitUnit'
                  label=''
                >
                  <MenuItem value='GB'>GB</MenuItem>
                  <MenuItem value='ABSOLUTE'>ABSOLUTE</MenuItem>
                  <MenuItem value='PERCENT'>PERCENT</MenuItem>
                </Select>
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={submitForm}>Save changes</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
interface NewBenefitTypeFormikProps {
  benefitSummary: string
  benefitCategory: string
  benefitProductID?: string
  benefitValue: string
  benefitUnit: string
}
